import type {
  ActionNavItem,
  InterfaceEntity,
  NavItem as NavItemDef,
  UserMenuNavItem,
} from '@unifyapps/defs/types/interface';
import type { InterfaceModes } from '../../../no-code/stores/InterfaceStore/types';
import type { NavAction, NavigationItem, NavUserMenu } from '../types';
import { getNavItemWithAdaptedUrl } from './getNavItemWithAdaptedUrl';

export type NavTransformVariant = 'primary' | 'secondary';

export type NavTransformUser = { name?: string; email?: string; id?: string };

export type NavTransformCustomizers = {
  buildUserMenu: (item: UserMenuNavItem, ctx: { user: NavTransformUser }) => NavUserMenu;
  buildAction: (item: ActionNavItem) => NavAction;
};

export type NavTransformContext = {
  interfaceRecord: InterfaceEntity;
  mode: InterfaceModes;
  variant: NavTransformVariant;
  customizers: NavTransformCustomizers;
  user: NavTransformUser;
};

export type TransformedNavItem = NavigationItem | NavItemDef;

// Transforms a single NavItem into its rendered form, or null to drop it.
// Rules diverge by variant: GROUP renders only in primary, USER_MENU and
// ACTION route through customizers only in secondary.
export function transformNavItem(
  item: NavItemDef,
  ctx: NavTransformContext,
): TransformedNavItem | null {
  const { interfaceRecord, mode, variant, customizers, user } = ctx;

  switch (item.type) {
    case 'MODULE':
    case 'URL':
    case 'PAGE':
      return getNavItemWithAdaptedUrl({ navItem: item, interfaceRecord, mode });

    case 'GROUP': {
      if (variant === 'secondary') return null;
      const nested = item.items
        .map((nestedItem) => {
          if (
            nestedItem.type === 'PAGE' ||
            nestedItem.type === 'URL' ||
            nestedItem.type === 'MODULE'
          ) {
            return getNavItemWithAdaptedUrl({ navItem: nestedItem, interfaceRecord, mode });
          }
          return null;
        })
        .filter(Boolean) as NavItemDef[];
      return { ...item, items: nested };
    }

    case 'USER_MENU':
      if (variant === 'primary') return null;
      return customizers.buildUserMenu(item, { user });

    case 'ACTION':
      if (variant === 'primary') return item;
      return customizers.buildAction(item);

    default:
      return item;
  }
}
